import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createChildParentMapping, computeChecked, computeUnChecked, computeIndeterminate } from '../common/helper/checkable';
export const TreeViewContext = /*#__PURE__*/React.createContext();
export function TreeViewProvider(_ref) {
  let {
    treeNodes = [],
    children,
    checkedKeys = [],
    expandedKeys = [],
    selectedKeys = [],
    expandAll = false,
    checkable = false,
    autoCheck = {},
    autoUnCheck = {}
  } = _ref;
  const [checked, setChecked] = useState(new Set([]));
  const [indeterminate, setIndeterminate] = useState(new Set([]));
  const [expanded, setExpanded] = useState([]);
  const [selected, setSelected] = useState([]);
  const {
    parentChildrenMap,
    childParentsMap
  } = useMemo(() => {
    if (checkable || !checkable && expandAll) {
      // create child-parents map, parent-children map only for expandAll case and checkable case
      // as it will not be needed in other cases
      return createChildParentMapping(treeNodes);
    }
    return {};
  }, [JSON.stringify(treeNodes), checkable, expandAll]);
  useEffect(() => {
    if (JSON.stringify(checkedKeys) != JSON.stringify([...checked])) {
      setChecked(new Set(checkedKeys));
      getIndeterminate(checkedKeys);
    }
    if (!expandAll && JSON.stringify(expandedKeys) != JSON.stringify(expanded)) {
      setExpanded(expandedKeys || []);
    }
    if (JSON.stringify(selectedKeys) != JSON.stringify(selected)) {
      setSelected(selectedKeys);
    }
  }, [JSON.stringify(checkedKeys), JSON.stringify(expandedKeys), JSON.stringify(selectedKeys), expandAll]);
  useEffect(() => {
    if (expandAll) {
      setExpanded([...new Set(Object.values(childParentsMap || {}).reduce((acc, curr) => {
        return [...acc, ...curr];
      }, []))]);
    }
  }, [expandAll]);
  const getChecked = function () {
    let treeNode = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let finalCheckboxStates = computeChecked(treeNode?.id, checked, indeterminate, childParentsMap, parentChildrenMap);

    // check if any dependent checkboxes need to be auto checked
    const dependentCheckboxes = autoCheck[treeNode?.id];
    dependentCheckboxes?.forEach(dependentId => {
      if (!finalCheckboxStates?.newChecked.has(dependentId)) {
        finalCheckboxStates = computeChecked(dependentId, finalCheckboxStates?.newChecked, finalCheckboxStates?.newIndeterminate, childParentsMap, parentChildrenMap);
      }
    });
    const {
      newChecked,
      newIndeterminate
    } = finalCheckboxStates;
    setChecked(newChecked);
    setIndeterminate(newIndeterminate);
    return {
      newChecked,
      newIndeterminate
    };
  };
  const getIndeterminate = function () {
    let initialChecked = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    const newIndeterminate = computeIndeterminate(initialChecked, childParentsMap);
    setIndeterminate(new Set(newIndeterminate));
  };
  const getUnChecked = function () {
    let treeNode = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let finalCheckboxStates = computeUnChecked(treeNode?.id, checked, indeterminate, childParentsMap, parentChildrenMap);

    // check if any dependent checkboxes need to be auto unchecked
    const dependentCheckboxes = autoUnCheck[treeNode?.id];
    dependentCheckboxes?.forEach(dependentId => {
      if (finalCheckboxStates?.newChecked.has(dependentId)) {
        finalCheckboxStates = computeUnChecked(dependentId, finalCheckboxStates?.newChecked, finalCheckboxStates?.newIndeterminate, childParentsMap, parentChildrenMap);
      }
    });
    const {
      newChecked,
      newIndeterminate
    } = finalCheckboxStates;
    setChecked(newChecked);
    setIndeterminate(newIndeterminate);
    return {
      newChecked,
      newIndeterminate
    };
  };
  return /*#__PURE__*/React.createElement(TreeViewContext.Provider, {
    value: {
      treeNodes,
      checked,
      expanded,
      setExpanded,
      selected,
      setSelected,
      indeterminate,
      getChecked,
      getUnChecked
    }
  }, children);
}
TreeViewProvider.propTypes = {
  treeNodes: PropTypes.array,
  children: PropTypes.node,
  checkedKeys: PropTypes.array,
  expandedKeys: PropTypes.array,
  selectedKeys: PropTypes.array,
  expandAll: PropTypes.bool,
  checkable: PropTypes.bool,
  autoCheck: PropTypes.object,
  autoUnCheck: PropTypes.object,
  finder: PropTypes.bool
};